import { Input, useDebounceCallback } from "@devhop/ui";
import { SearchIcon } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { useEffect, useState } from "react";

export type OTApprovalSearchParams = {
  offset?: number;
  limit?: number;
  sort?: Array<{ field: string; dir: "asc" | "desc" }>;
  periodYear?: number;
  periodMonth?: number;
  status?: string;
  search?: string;
  employeeId?: string;
};

const VALUE_ALL = "__all__";
const STATUS_OPTIONS = [
  { value: "pending", label: "ລໍຖ້າອະນຸມັດ" },
  { value: VALUE_ALL, label: "ທັງໝົດ" },
  { value: "approved", label: "ອະນຸມັດແລ້ວ" },
  { value: "rejected", label: "ປະຕິເສດ" },
];

const MONTHS = [
  { value: "1", label: "ມັງກອນ" },
  { value: "2", label: "ກຸມພາ" },
  { value: "3", label: "ມີນາ" },
  { value: "4", label: "ເມສາ" },
  { value: "5", label: "ພຶດສະພາ" },
  { value: "6", label: "ມິຖຸນາ" },
  { value: "7", label: "ກໍລະກົດ" },
  { value: "8", label: "ສິງຫາ" },
  { value: "9", label: "ກັນຍາ" },
  { value: "10", label: "ຕຸລາ" },
  { value: "11", label: "ພະຈິກ" },
  { value: "12", label: "ທັນວາ" },
];

type OTApprovalFilterProps = {
  searchParams: OTApprovalSearchParams;
  fromRoute: "/app/hr/overtime/approvals";
};

export function OTApprovalFilter({
  searchParams,
  fromRoute,
}: OTApprovalFilterProps) {
  const nav = useNavigate({ from: fromRoute });
  const search = searchParams;
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const [searchInput, setSearchInput] = useState(search.search ?? "");
  const [statusInput, setStatusInput] = useState(search.status ?? "pending");
  const [yearInput, setYearInput] = useState(
    String(search.periodYear ?? currentYear),
  );
  const [monthInput, setMonthInput] = useState(
    String(search.periodMonth ?? currentMonth),
  );

  useEffect(() => {
    setSearchInput(search.search ?? "");
    setStatusInput(search.status ?? "pending");
    setYearInput(String(search.periodYear ?? currentYear));
    setMonthInput(String(search.periodMonth ?? currentMonth));
  }, [
    search.search,
    search.status,
    search.periodYear,
    search.periodMonth,
    currentYear,
    currentMonth,
  ]);

  const doNav = (opts: Partial<{ year: string; month: string; status: string; searchVal: string }> = {}) => {
    const yr = opts.year ?? yearInput;
    const mon = opts.month ?? monthInput;
    const st = opts.status ?? statusInput;
    const sv = opts.searchVal ?? searchInput;
    nav({
      search: {
        offset: 0,
        limit: search.limit ?? 20,
        sort: search.sort,
        periodYear: Number(yr) || currentYear,
        periodMonth: Number(mon) || currentMonth,
        status: st === VALUE_ALL ? undefined : st,
        search: sv.trim() || undefined,
        employeeId: search.employeeId ?? undefined,
      },
    });
  };

  const debouncedSearch = useDebounceCallback((val: string) => doNav({ searchVal: val }), 400);

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <SearchIcon className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="ຄົ້ນຫາພະນັກງານ..."
            className="h-8 pl-8 sm:max-w-2xs"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              debouncedSearch(e.target.value);
            }}
          />
        </div>
        <Input
          type="number"
          min={2000}
          max={2100}
          value={yearInput}
          onChange={(e) => {
            setYearInput(e.target.value);
            doNav({ year: e.target.value });
          }}
          placeholder="ປີ"
          className="h-8 w-full sm:w-24"
        />
        <SimpleSelect
          options={MONTHS}
          value={monthInput}
          onValueChange={(v) => {
            setMonthInput(v);
            doNav({ month: v });
          }}
          placeholder="ເດືອນ"
          className="h-8 w-full sm:w-32"
        />
        <SimpleSelect
          options={STATUS_OPTIONS}
          value={statusInput}
          onValueChange={(v) => {
            setStatusInput(v);
            doNav({ status: v });
          }}
          placeholder="ສະຖານະ"
          className="h-8 w-full sm:w-32"
        />
      </div>
    </div>
  );
}
